import { useState } from 'react';
import classnames from 'classnames';
import { uniqueId } from 'lodash';

import { EventAction, FieldRenderer } from 'plugins/types';
import IcomoonIcon from 'components/common/Icon/IcomoonIcon';
import { ICON_COLORS } from 'styles/stylesProvider/themes';

import EventActionsExecutor from '../actions/sentinel-one/EventActionsExecutor';
import { processGraphFieldToActionMapper } from '../actions/sentinel-one/utils';
import { TooltipPlacement, TruncateWithTooltip } from '../components';
import { EmptyFieldRenderer } from './EmptyFieldRenderer';

import './ProcessFieldRenderer.scss';

const processNameFieldMap: Record<string, string> = {
  'src.process.uid': 'src.process.name',
  'src.process.parent.uid': 'src.process.parent.name',
  'osSrc.process.uid': 'osSrc.process.name',
  'osSrc.process.parent.uid': 'osSrc.process.parent.name',
  'tgt.process.uid': 'tgt.process.name',
};

export const ProcessFieldRenderer: FieldRenderer = ({ fieldId, row, value = null }) => {
  const [selectedAction, setSelectedAction] = useState<EventAction | null>(null);
  const processNameFieldId = processNameFieldMap[fieldId];
  const processNameValue = row[processNameFieldId];

  const action = processGraphFieldToActionMapper(fieldId);
  const isActionable = action?.displayAction && action.displayAction(row, fieldId);
  const processValuesClassNames = classnames('ProcessFieldRenderer__Values', {
    'ProcessFieldRenderer__Values--actionable': isActionable,
  });
  const rightIconClassNames = classnames('ProcessFieldRenderer__RightIcon', {
    'ProcessFieldRenderer__RightIcon--actionable': isActionable,
  });

  if (value === null) {
    return <EmptyFieldRenderer />;
  }

  const handleClick = () => {
    if (isActionable) {
      setSelectedAction(action);
    }
  };

  return (
    <div className="ProcessFieldRenderer" onClick={handleClick}>
      <span className="ProcessFieldRenderer__LeftIcon">
        <IcomoonIcon name="processes_ic" size={16} />
      </span>
      <div className={processValuesClassNames}>
        <span data-term-field={processNameFieldId} data-term-value-type="string">
          <TruncateWithTooltip
            id={uniqueId(`ProcessFieldRenderer-Name-${processNameValue}-`)}
            text={processNameValue?.toString() ?? ''}
            placement={TooltipPlacement.BOTTOM}
            classNames={'ProcessFieldRenderer__Name'}
          />
        </span>
        <span className="ProcessFieldRenderer__UUID" data-term-field={fieldId} data-term-value-type="string">
          <TruncateWithTooltip
            id={uniqueId(`ProcessFieldRenderer-UUID-${value}-`)}
            text={value?.toString() ?? ''}
            placement={TooltipPlacement.BOTTOM}
          />
        </span>
      </div>
      <span className={rightIconClassNames}>
        <IcomoonIcon
          name="process-graph"
          size={16}
          color={isActionable ? ICON_COLORS.LINK : ICON_COLORS.INACTIVE_ENDPOINT}
        />
      </span>
      {isActionable && (
        <EventActionsExecutor
          action={selectedAction}
          values={row}
          actionCompleteCallback={() => {
            setSelectedAction(null);
          }}
        />
      )}
    </div>
  );
};
